/**
 * Shared scaffold manifest builder for the strategy/planning vertical.
 *
 * The four reference manifests (gantt_task, gantt_milestone, sc_option,
 * sc_criterion) share a minimal rounded-rect SVG renderer with an accent
 * stripe. Epic SP-A and SP-B replace `renderers.svg` per manifest with
 * the real domain-specific renderers (task bars with progress fills,
 * milestone diamonds, scorecard cards with rank badges, radar facets).
 */

import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';

export const planningNodePropsSchema = z.object({
  name: z.string().default(''),
  domain: z.string().default('gantt'),
  stereotype: z.string().optional(),
  fillColor: z.string().default('#FFFFFF'),
  strokeColor: z.string().default('#94a3b8'),
  textColor: z.string().default('#1e293b'),
  accentColor: z.string().default('#3b82f6'),
});

export type PlanningNodeProps = z.infer<typeof planningNodePropsSchema>;

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderPlanningNodeSvg(
  viewModel: { width?: number; height?: number },
  props: Record<string, unknown>,
): string {
  const w = viewModel.width ?? 220;
  const h = viewModel.height ?? 48;
  const name = String(props.name || 'Item');
  const domain = String(props.domain || '');
  const accent = String(props.accentColor || '#3b82f6');
  const fill = String(props.fillColor || '#FFFFFF');
  const stroke = String(props.strokeColor || '#94a3b8');
  const textColor = String(props.textColor || '#1e293b');

  return [
    `<rect x="0" y="0" width="${w}" height="${h}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="1.5" />`,
    `<rect x="0" y="0" width="4" height="${h}" rx="2" ry="2" fill="${accent}" />`,
    `<text x="14" y="${Math.round(h / 2 + 4)}" font-size="13" font-weight="600" fill="${textColor}" font-family="Arial, sans-serif">${escape(name)}</text>`,
    `<text x="14" y="${h - 8}" font-size="9" fill="#64748b" font-family="Arial, sans-serif">${escape(domain.toUpperCase())}</text>`,
  ].join('');
}

export interface PlanningManifestSpec {
  id: string;
  domain: string;
  label: string;
  accent: string;
  icon?: string;
  defaultWidth?: number;
  defaultHeight?: number;
}

export function createPlanningManifest(spec: PlanningManifestSpec): ShapeManifest<PlanningNodeProps> {
  return {
    id: spec.id,
    version: '1.0.0',
    category: 'planning',
    icon: spec.icon ?? 'box',
    propsSchema: planningNodePropsSchema,
    defaultProps: () => ({
      name: spec.label,
      domain: spec.domain,
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: spec.accent,
    }),
    defaultDimensions: {
      width: spec.defaultWidth ?? 220,
      height: spec.defaultHeight ?? 48,
    },
    anchors: {
      snap: ['top', 'right', 'bottom', 'left', 'center'],
      perimeter: 'rect',
    },
    capabilities: {
      selectable: true,
      transformable: true,
      textEditable: true,
      connectable: true,
    },
    properties: [
      { key: 'name', label: 'Name', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
      { key: 'stereotype', label: 'Stereotype', kind: 'string', ui: { group: 'identity', priority: 2 } },
    ],
    acl: () => true,
    renderers: {
      svg: (args: { viewModel: unknown; props: unknown }) =>
        renderPlanningNodeSvg(
          args.viewModel as { width?: number; height?: number },
          args.props as Record<string, unknown>,
        ),
    },
  };
}
